// @givmo/app-charity — the §321 fundraiser organizer + message card (FCA-WEB1 PR-B, FCA-WEB3).
//
// ONE composite card for the fundraiser's shared identity and its optional creator message, rendered
// under an expanded fundraiser group on the unified statement (`statement-fundraiser-panel.tsx`).
//
// It began as part of the legacy statement page's fundraiser-attribution SECTION — a list of
// decorated rows behind the `VITE_FUNDRAISER_ROWS_UI_ENABLED` knob. CPA-BE16 retired that page and
// the section with it; the cards survived because the certified table renders the same three pieces
// of supporter content inside its own groups. The knob did not retire with the section: the statement
// page reads it directly (`statement-page.tsx`), so the organizer/message panels stay dark exactly as
// before.
//
// ★ IDENTITY LAW: `organizer` and `message` are null unless the backend's §318 dual gate opened them
// (the charity is consented AND that donor opted to share). This UI only renders what the backend
// returns — it never fabricates, infers, or backfills an identity.
//
// ★ FRAMING IS A CONDITION, NOT DECORATION (FCA-LEG2 condition 2): a creator message renders ONLY
// beside its server-authored framing. No framing, no message — a supporter's words must never appear
// unattributed.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  charityAdminGetSection321FundraiserDetailOptions,
  charityAdminListSection321FundraisersOptions,
} from '@givmo/api-client/query';
import { isGivmoError, sdk, type Section321FundraiserOrganizerOut } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Icon, cn, testIdProp } from '@givmo/design-system';
import { Flag, Info } from 'lucide-react';

const PREFIX = 'grants-fundraiser';
// ── The fundraiser organizer's shared identity + optional message — ONE composite card ──────────────
export function FundraiserDetailsCard({
  organizer,
  message,
  framing,
  charityId,
  fundraiserId,
  className,
}: {
  organizer: Section321FundraiserOrganizerOut | null | undefined;
  message: string | null | undefined;
  framing: string | null;
  charityId: string;
  fundraiserId: string;
  className?: string;
}) {
  const normalizedMessage =
    typeof message === 'string' && message.trim().length > 0 && framing !== null
      ? { message, framing }
      : null;

  if (!organizer && !normalizedMessage) return null;

  return (
    <section
      aria-label="Fundraiser organizer details"
      className={cn(
        'mb-12 overflow-hidden rounded-lg border border-border-subtle bg-surface-sunken',
        className,
      )}
      {...testIdProp(`${PREFIX}-details-card`)}
    >
      {organizer && <FundraiserOrganizer organizer={organizer} />}
      {organizer && normalizedMessage && (
        <div aria-hidden className="border-t border-border-subtle" />
      )}
      {normalizedMessage && (
        <FundraiserMessage
          message={normalizedMessage.message}
          framing={normalizedMessage.framing}
          charityId={charityId}
          fundraiserId={fundraiserId}
        />
      )}
    </section>
  );
}

// ── The fundraiser creator's optional message — VERBATIM plain text, never markdown/HTML ──────────────
export function FundraiserMessage({
  message,
  framing,
  charityId,
  fundraiserId,
}: {
  message: string;
  /** FCA-WEB3: REQUIRED and non-nullable on purpose — there is no way to render this panel without
   *  the framing, so "a message never appears unattributed" is enforced by the compiler rather than
   *  by remembering to check at each call site. */
  framing: string;
  charityId: string;
  fundraiserId: string;
}) {
  return (
    <section className="p-14" {...testIdProp(`${PREFIX}-message`)}>
      {/* The header row: the label on the left and, on the right and vertically in line with it, the
          (i) that opens the signed framing on demand, then the Flag (Jonathan, 2026-09-01: the
          framing line under every message was too prominent; nothing renders under the message any
          more, so the card ends at its own padding). Both controls live ON the message panel, so a
          null/suppressed message takes the framing and the remedy away with the words. The 32px hit
          targets overhang the 16px label row by 8px each way — inside the section's 14px padding —
          so the row stays the label's height. DOM order: label, (i), Flag. */}
      <div className="mb-6 flex items-center justify-between gap-8">
        <h3 className="flex items-center gap-6 font-sans text-s12 font-w600 uppercase tracking-wide text-text-secondary">
          Organizer message
        </h3>
        <div className="-my-8 flex shrink-0 items-center gap-2">
          <FundraiserMessageFraming framing={framing} />
          <FundraiserMessageObjection charityId={charityId} fundraiserId={fundraiserId} />
        </div>
      </div>
      {/* PLAIN TEXT ONLY: `message` is a React text node — auto-escaped, never parsed as markdown or
          HTML, and never passed through dangerouslySetInnerHTML. A message containing "<script>…"
          or "**bold**" renders those characters literally; neither can ever become a real element. */}
      <p className="whitespace-pre-wrap font-sans text-s14 leading-relaxed tracking-givmo text-text-strong">
        {message}
      </p>
    </section>
  );
}

/** The one 32px icon-button recipe both message actions share (the Flag's, unchanged). */
const MESSAGE_ACTION_BUTTON =
  'inline-flex size-32 items-center justify-center rounded-md text-icon outline-none transition-colors hover:bg-surface-hover hover:text-text-secondary focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500';

// ── FCA-WEB3 (FCA-LEG2 Q4): the attribution / non-endorsement / non-binding framing, on demand ────────
// The framing is REQUIRED and message-coupled: `FundraiserMessage` cannot render without it, so this
// (i) exists beside every message and never without one. The copy is SERVER-AUTHORED — a versioned
// President-signed disclaimer row on the wire as `message_framing` — never a string in this repo,
// never composed or re-wrapped here. Same PLAIN-TEXT LAW as the message: a React text node,
// auto-escaped, never markdown, never dangerouslySetInnerHTML. The modal mirrors the Flag's: the
// design system's Dialog (role dialog, aria-modal, focus trap, Escape, focus back to the (i)).
function FundraiserMessageFraming({ framing }: { framing: string }) {
  return (
    <Dialog.Root>
      <Dialog.Trigger
        aria-label="About this message"
        className={MESSAGE_ACTION_BUTTON}
        {...testIdProp(`${PREFIX}-message-info-button`)}
      >
        <Icon icon={Info} size={14} aria-hidden />
      </Dialog.Trigger>
      <Dialog.Content
        aria-label="About this message"
        className="flex flex-col gap-16"
        testId={`${PREFIX}-message-info-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          About this message
        </Dialog.Title>
        <Dialog.Description
          className="font-sans text-s15 font-w400 leading-relaxed tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-message-framing`)}
        >
          {framing}
        </Dialog.Description>
        <div className="mt-8 flex items-center justify-end">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${PREFIX}-message-info-close`)}
          >
            Close
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── FCA-BE9: object to a fundraiser's creator-message ─────────────────────────────────────────────────
// One click → a lightweight confirm → POST the charity-admin objection route. On success the backend
// suppresses the message GLOBALLY (every recipient charity) at the next §321 read, so we invalidate BOTH
// this fundraiser's detail (its `message` goes null → this whole panel self-hides) AND the list (its
// `has_message` badge clears). Idempotent server-side: a repeat while one objection is OPEN returns the
// existing one (still a 2xx). Errors self-contain to an inline message — never a toast (parity with the
// section's other failure paths), and the dialog stays open so the charity can retry or cancel.
function FundraiserMessageObjection({
  charityId,
  fundraiserId,
}: {
  charityId: string;
  fundraiserId: string;
}) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const object = useMutation({
    mutationFn: () =>
      sdk.charityAdminObjectToFundraiserMessage({
        path: { charity_id: charityId, fundraiser_id: fundraiserId },
        throwOnError: true,
      }),
    onSuccess: async () => {
      setConfirmOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: charityAdminGetSection321FundraiserDetailOptions({
            path: { charity_id: charityId, fundraiser_id: fundraiserId },
          }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: charityAdminListSection321FundraisersOptions({
            path: { charity_id: charityId },
          }).queryKey,
        }),
      ]);
    },
    onError: (error: unknown) =>
      setServerError(
        isGivmoError(error) ? error.message : 'Something went wrong. Please try again.',
      ),
  });

  // Once the objection lands the global-suppression refetch typically unmounts this whole panel; until
  // it settles we hold a brief acknowledgment in place of the button so the click has visible feedback.
  if (object.isSuccess) {
    return (
      <p
        className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary"
        {...testIdProp(`${PREFIX}-message-objection-ack`)}
      >
        Message hidden pending review
      </p>
    );
  }

  return (
    <div className="shrink-0">
      <button
        type="button"
        aria-label="Object to this message"
        onClick={() => {
          setServerError(null);
          setConfirmOpen(true);
        }}
        className={MESSAGE_ACTION_BUTTON}
        {...testIdProp(`${PREFIX}-message-objection-button`)}
      >
        <Icon icon={Flag} size={14} aria-hidden />
      </button>
      <Dialog.Root
        open={confirmOpen}
        onOpenChange={(next) => !object.isPending && setConfirmOpen(next)}
      >
        <Dialog.Content
          aria-label="Object to this message"
          className="flex flex-col gap-16"
          testId={`${PREFIX}-message-objection-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            Object to this message
          </Dialog.Title>
          <p className="font-sans text-s15 font-w400 leading-relaxed tracking-givmo text-text-secondary">
            Objecting hides this message for all charities while Givmo reviews it.
          </p>
          {serverError !== null && (
            <ErrorMessage testId={`${PREFIX}-message-objection-error`}>{serverError}</ErrorMessage>
          )}
          <div className="mt-8 flex items-center justify-end gap-8">
            <Dialog.Close
              disabled={object.isPending}
              render={<Button appearance="saas" tone="secondary" size="md" />}
              {...testIdProp(`${PREFIX}-message-objection-cancel`)}
            >
              Cancel
            </Dialog.Close>
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={object.isPending}
              onClick={() => {
                setServerError(null);
                object.mutate();
              }}
              testId={`${PREFIX}-message-objection-confirm`}
            >
              Object
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}

// ── FCA-BE8 (§317(a)): the fundraiser creator's shared name + contact ─────────────────────────────────
// Rendered only when the backend emits a non-null `organizer` (both fields NOT NULL — the creator is a
// registered user). Name + email are plain React text nodes (auto-escaped); the email is mailto-linked
// but never HTML-interpreted, so a value carrying markup renders literally, never as an element.
//
// FCA-WEB4 (FCA-BE12, D-FCM-15): the same object carries an ORGANIZATION that owns the fundraiser — a
// company sharing its public name and the contact email it chose — marked `kind: 'organization'`. The
// Charity Terms call it a fundraiser organizer too, so the heading stands; the name gains a quiet
// "Organization" beside it, so a charity knows it is thanking an organization rather than a person.
// Only that exact value qualifies: `'person'`, an absent kind (a backend before FCA-BE12) or a kind
// this build does not know all render the person layout, unchanged.
export function FundraiserOrganizer({
  organizer,
}: {
  organizer: Section321FundraiserOrganizerOut;
}) {
  return (
    <section className="p-14" {...testIdProp(`${PREFIX}-organizer`)}>
      <h3 className="mb-6 flex items-center gap-6 font-sans text-s12 font-w600 uppercase tracking-wide text-text-secondary">
        Fundraiser organizer
      </h3>
      <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
        {organizer.name}
        {organizer.kind === 'organization' && (
          <span
            className="whitespace-nowrap font-w400 text-text-secondary"
            {...testIdProp(`${PREFIX}-organizer-kind`)}
          >
            {' '}
            <span aria-hidden="true">·</span>
            <span className="sr-only">,</span> Organization
          </span>
        )}
      </p>
      <a
        href={`mailto:${organizer.email}`}
        className="font-sans text-s13 font-w400 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
        {...testIdProp(`${PREFIX}-organizer-email`)}
      >
        {organizer.email}
      </a>
    </section>
  );
}
